import { useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { BudgetListItem } from '@fdh/shared';
import { useLanguage } from '../../../contexts/LanguageContext';
import type { EmployerMoneyGivenCardView } from '../../../utils/employerMoneyGivenSettlement';
import {
  formatEmployerHistorySettlementResult,
  formatEmployerMoneyAmount,
  shouldHideBudgetEditBySettlement,
} from '../../../utils/employerMoneyGivenSettlement';
import type { SettlementListItem } from '@fdh/shared';

type MoneyGivenCombinedCardProps = {
  row: BudgetListItem;
  cardView: EmployerMoneyGivenCardView;
  settlements: SettlementListItem[];
  fromPath: string;
  periodLabel: string;
  formatCreatedAtLabel: (createdAt: string | undefined) => string;
  formatSubItemCreatedAtLabel?: (createdAt: string | undefined) => string;
  includesAddOnLabel: string;
  moneyGivenAmountLabel: string;
  settlementResultLabel: string;
  mainAmountLabel: string;
  subAmountLabel: string;
  addOnLabel: string;
  editAriaLabel: string;
  expandAriaLabel: string;
  collapseAriaLabel: string;
  badgeUnsettledLabel: string;
  badgePendingReviewLabel: string;
  badgeSettledLabel: string;
  onNavigateDetail: (path: string) => void;
  /** Helper read-only list: hide add / edit / expand controls and add-on summary. */
  readOnly?: boolean;
  /** When set, replaces default employer badge labels (null hides badge). */
  badgeLabelOverride?: string | null;
};

const PencilIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="h-6 w-6 shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export const MoneyGivenCombinedCard = ({
  row,
  cardView,
  settlements,
  fromPath,
  periodLabel,
  formatCreatedAtLabel,
  formatSubItemCreatedAtLabel,
  includesAddOnLabel,
  moneyGivenAmountLabel,
  settlementResultLabel,
  mainAmountLabel,
  subAmountLabel,
  addOnLabel,
  editAriaLabel,
  expandAriaLabel,
  collapseAriaLabel,
  badgeUnsettledLabel,
  badgePendingReviewLabel,
  badgeSettledLabel,
  onNavigateDetail,
  readOnly = false,
  badgeLabelOverride,
}: MoneyGivenCombinedCardProps) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const hasSubItems = Boolean(row.isMergedGroup && row.mergedChildCount && row.mergedChildCount > 0);
  const hideEditButton = shouldHideBudgetEditBySettlement(row.startDate, row.endDate, settlements);
  const disableBudgetActions = hideEditButton || row.isSettled === true;
  const showUnsettledMainEdit =
    cardView.badge === 'unsettled' && !row.isLocked && row.isSettled !== true && !row.parentBudgetId;
  const subChildren = row.children?.filter((child) => Boolean(child.parentBudgetId)) ?? [];
  const subTotal = subChildren.reduce((sum, child) => sum + child.amount, 0);
  const mainOnlyAmount = Math.max(0, row.amount - subTotal);

  const handleStopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleMainNavigate = () => {
    onNavigateDetail(cardView.detailPath);
  };

  const handleMainKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMainNavigate();
    }
  };

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const handleToggleExpandKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleExpand();
    }
  };

  const badgeLabel =
    badgeLabelOverride !== undefined
      ? badgeLabelOverride ?? ''
      : cardView.badge === 'pending_review'
        ? badgePendingReviewLabel
        : cardView.badge === 'settled'
          ? badgeSettledLabel
          : badgeUnsettledLabel;

  const showBadge =
    badgeLabelOverride !== undefined ? Boolean(badgeLabelOverride) : Boolean(cardView.badge);

  const badgeClass =
    cardView.badge === 'pending_review'
      ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200'
      : cardView.badge === 'settled'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200'
        : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200';

  const calendarIconClass =
    cardView.badge === 'pending_review'
      ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
      : cardView.badge === 'settled'
        ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
        : 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-200';

  const balanceClass =
    (cardView.activeSettlement?.balance ?? 0) >= 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-red-600 dark:text-red-400';

  const resolveSubItemCreatedAtLabel =
    formatSubItemCreatedAtLabel ?? formatCreatedAtLabel;

  return (
    <article className="overflow-hidden rounded-xl border border-gray-300/80 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
      <div
        className="cursor-pointer p-4"
        role="button"
        tabIndex={0}
        aria-label={periodLabel}
        onClick={handleMainNavigate}
        onKeyDown={handleMainKeyDown}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
              <span className={`rounded-lg p-1.5 ${calendarIconClass}`}>
                <CalendarIcon />
              </span>
              <span>{periodLabel}</span>
            </div>
            <p className="ml-9 mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {formatCreatedAtLabel(row.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-2" onClick={handleStopPropagation}>
            {!readOnly && !disableBudgetActions && !row.parentBudgetId && !row.isLocked && row.isSettled !== true && !hideEditButton ? (
              <Link
                to={`/budgets/new?parentBudgetId=${encodeURIComponent(row.id)}`}
                className="inline-flex min-h-9 items-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:border-emerald-600 dark:hover:bg-emerald-900/50 dark:hover:text-emerald-100"
                aria-label={addOnLabel}
              >
                {addOnLabel}
              </Link>
            ) : null}
            {!readOnly && showUnsettledMainEdit && !hasSubItems ? (
              <Link
                to={`/budgets/${row.id}/edit`}
                state={{ from: fromPath }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 shadow-sm hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                aria-label={editAriaLabel}
              >
                <PencilIcon />
              </Link>
            ) : null}
            {!readOnly && hasSubItems ? (
              <button
                type="button"
                onClick={handleToggleExpand}
                onKeyDown={handleToggleExpandKeyDown}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-primary-300"
                aria-label={expanded ? collapseAriaLabel : expandAriaLabel}
                aria-expanded={expanded}
              >
                {expanded ? (
                  <span aria-hidden className="text-lg leading-none">
                    −
                  </span>
                ) : (
                  <span aria-hidden className="text-sm font-bold">
                    i
                  </span>
                )}
              </button>
            ) : null}
          </div>
        </div>

        <div className="ml-9 flex flex-col gap-2.5">
          {!readOnly && hasSubItems ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">{includesAddOnLabel}</p>
          ) : null}
          {showBadge ? (
            <div className="flex items-center justify-between gap-2">
              <span
                className={`inline-flex w-fit items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold ${badgeClass}`}
              >
                {badgeLabel}
              </span>
              <ChevronRightIcon />
            </div>
          ) : null}
        </div>

        <div className="ml-9 mt-3 border-t border-gray-200/40 pt-3 dark:border-gray-700/40">
          {cardView.showSettlementResult && cardView.activeSettlement ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('settlement.totalSpent')}
                </span>
                <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                  {formatEmployerMoneyAmount(cardView.activeSettlement.totalSpent)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{moneyGivenAmountLabel}</span>
                <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                  {formatEmployerMoneyAmount(row.amount)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{settlementResultLabel}</span>
                <span className={`text-lg font-bold tracking-tight ${balanceClass}`}>
                  {formatEmployerHistorySettlementResult(cardView.activeSettlement.balance, t)}
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{moneyGivenAmountLabel}</span>
              <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                {formatEmployerMoneyAmount(row.amount)}
              </span>
            </div>
          )}
        </div>
      </div>

      {!readOnly && hasSubItems && expanded && row.children ? (
        <div
          className="border-t border-gray-200/40 bg-gray-50/80 p-4 dark:border-gray-700/40 dark:bg-gray-950/40"
          onClick={handleStopPropagation}
        >
          <ul className="flex flex-col gap-3" role="list">
            <li className="rounded-xl border border-orange-200/50 bg-orange-50/50 p-3 shadow-sm dark:border-orange-900/40 dark:bg-orange-950/20">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-[13px] font-bold text-gray-900 dark:text-gray-100">
                  {periodLabel}
                </span>
                {showUnsettledMainEdit ? (
                  <Link
                    to={`/budgets/${row.id}/edit`}
                    state={{ from: fromPath }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 shadow-sm hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                    aria-label={editAriaLabel}
                  >
                    <PencilIcon />
                  </Link>
                ) : null}
              </div>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                {resolveSubItemCreatedAtLabel(row.createdAt)}
              </p>
              <div className="mt-2 flex items-center justify-between pl-1">
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">{mainAmountLabel}</span>
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                  {formatEmployerMoneyAmount(mainOnlyAmount)}
                </span>
              </div>
            </li>
            {subChildren.map((child) => {
              const hideChildEdit = shouldHideBudgetEditBySettlement(child.startDate, child.endDate, settlements);
              const disableChild = hideChildEdit || child.isSettled === true;
              return (
                <li
                  key={child.id}
                  className="rounded-xl border border-emerald-200/50 bg-emerald-50/40 p-3 shadow-sm dark:border-emerald-900/40 dark:bg-emerald-950/20"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-bold text-gray-900 dark:text-gray-100">
                      {child.startDate} – {child.endDate}
                    </span>
                    {!disableChild ? (
                      <Link
                        to={`/budgets/${child.id}/edit`}
                        state={{ from: fromPath }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 shadow-sm hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                        aria-label={editAriaLabel}
                      >
                        <PencilIcon />
                      </Link>
                    ) : null}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {resolveSubItemCreatedAtLabel(child.createdAt)}
                  </p>
                  <div className="mt-2 flex items-center justify-between pl-1">
                    <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">{subAmountLabel}</span>
                    <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                      {formatEmployerMoneyAmount(child.amount)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </article>
  );
};
